import { useEffect, useState } from 'react';

/** 後端 `GET /api/config` 回傳的執行期設定。 */
export interface AppConfig {
  llm_model?: string;
  vlm_model?: string;
  /** 單機版（Windows 安裝版）：免登入、無帳號與業者管理。 */
  single_user_mode?: boolean;
  /** 工具列「快速模式」開關的初始值（後端 agent_skills/.env 的 RUNTIME_FAST_PATH）。 */
  fast_path_default?: boolean;
}

// 模組層快取：多數欄位執行期不會變，整個 App 只取一次；
// 但 llm_model 會因「模型管理頁啟動模型 → apply」而改變，
// 由 refreshAppConfig() 重抓並通知所有掛載中的元件。
// inflight 讓同時掛載的多個元件共用同一個請求，不會各發一次。
// 失敗一律不寫入快取：初次載入失敗 → 下個元件掛載時自然重試；
// refresh 失敗 → 保留原有好值（絕不能把可用的設定蓋成空物件，
// 否則 single_user_mode 變 undefined 會讓單機版整個 UI 降級）。
let cached: AppConfig | null = null;
let inflight: Promise<AppConfig> | null = null;
// 世代序號：refresh 後讓仍在途的舊請求作廢，避免舊值蓋掉新值。
let generation = 0;
const listeners = new Set<(c: AppConfig) => void>();

/** 無視快取發出請求（在途請求仍共用）；成功才覆寫快取並通知。 */
function fetchConfig(): Promise<AppConfig> {
  if (!inflight) {
    const gen = ++generation;
    inflight = fetch('/api/config', { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return ((await r.json()) ?? {}) as AppConfig;
      })
      .then(
        (value) => {
          if (gen === generation) {
            cached = value;
            inflight = null;
            listeners.forEach((fn) => fn(value));
          }
          return value;
        },
        () => {
          // 失敗：不動快取、不通知；清 inflight 讓之後的載入/刷新可重試
          if (gen === generation) inflight = null;
          return cached ?? {};
        },
      );
  }
  return inflight;
}

function loadAppConfig(): Promise<AppConfig> {
  if (cached) return Promise.resolve(cached);
  return fetchConfig();
}

/**
 * 重抓 `/api/config`；成功才替換快取並同步通知所有使用 useAppConfig 的元件，
 * 失敗保留原值。用於模型管理頁切換模型成功後，讓對話頁的模型顯示即時跟上。
 */
export function refreshAppConfig(): Promise<AppConfig> {
  inflight = null; // 作廢在途舊請求（其世代序號已過期），強制發新請求
  return fetchConfig();
}

/**
 * 取得後端執行期設定；尚未載入時回傳 null。
 *
 * 注意：`single_user_mode` 未載入完成前為 undefined，判斷時應採「預設隱藏」
 * 的保守寫法，避免管理選項在單機版閃現一下才消失。
 */
export function useAppConfig(): AppConfig | null {
  const [config, setConfig] = useState<AppConfig | null>(cached);

  useEffect(() => {
    let alive = true;
    const fn = (c: AppConfig) => {
      if (alive) setConfig(c);
    };
    listeners.add(fn);
    if (cached) setConfig(cached);
    else void loadAppConfig();
    return () => {
      alive = false;
      listeners.delete(fn);
    };
  }, []);

  return config;
}
